// 지형 굴착 편집 저장소 — "셀 승격(cell promotion)"의 데이터 층 (SSOT).
//
// [왜 이런 구조인가]
//   하이트필드는 XZ 한 칸에 높이가 하나뿐이라 오버행·동굴을 표현할 수 없다.
//   그렇다고 세계 전체를 복셀로 바꿀 수는 없다 — 서울 39km×30km 를 1m 컬럼으로 세면 11.9억 개.
//   그래서 **편집이 실제로 일어난 8m 셀만** 1m 복셀 8×8(PROMOTE_SUB) 로 승격한다.
//   승격 직후엔 64칸이 전부 같은 밴드라 화면이 1픽셀도 안 변한다 → 안 건드린 땅 회귀 0.
//   (기획 전문: /tech → 레벨디자인 → 지형 굴착(Excavation))
//
// [좌표계] 셀은 "전역 셀 인덱스"(globalCellIdx)로 식별한다. 타일 윈도가 잘리고 LOD 가
//   셀을 병합해도 변하지 않는 절대 식별자여야 렌더(워커)·발밑·편집이 같은 셀을 가리킨다.
//
// [저장 단위] 셀당 Uint32Array(64 * CAVE_WORDS) — 1m 컬럼마다 **비트마스크**. 비트 k =
//   원지표에서 k 번째 아래 칸이 비었다. 128비트 = 지하 128m.
//   컬럼 하나가 낱말 여러 개라 값으로 못 다룬다 → 접근은 전부 hfConstants 의 col* 접근자로.
//   · 선두 연속 비트(0,1,2,…) = 위에서 깎아낸 깊이 → 노천 채굴·평탄화(P1 그대로)
//   · 중간에 낀 비트 = **흙 밑 빈 공간** → 위에 남은 흙이 곧 천장 = 동굴·오버행 (P2, C안)
//   숫자 하나("몇 칸 깎았다")였을 땐 빈 공간이 늘 맨 위에 붙어 천장을 적을 자리가 없었다.
//
// [현재 범위] 클라이언트 인메모리 + 세션 한정. DB(world_terrain_edit)·서버 권한
//   (owner_at 필지 검사)·멀티 동기화는 P1/P3. 지금은 승격 경로가 실제로 도는지 보는 스파이크.
import {
  PROMOTE_SUB, VOXEL_GRID_OFF, CAVE_BITS, CAVE_WORDS, PLAYER_CUT_BITS, globalCellIdx,
  colBit, colSetBit, colClrBit, colLead, colDigFace, colAny,
} from './hfConstants';

// 유저 곡괭이 한도 — 형식 용량(CAVE_BITS=128)이 아니라 **게임 규칙**이다(hfConstants 주석).
// 여기를 CAVE_BITS 로 되돌리면 수직갱 특례가 128m 까지 풀린다.
//
// [2026-08-02] 이 한도는 **하늘이 열린 깊이(lead)** 에만 문다. 암반 지붕 아래 칸은 아무리
//   파도 지표가 안 내려가므로 세지 않는다 — 이미 dig_terrain 주석이 "지표를 낮추는 건
//   컬럼당 6m 까지, **그 아래는 자유**" 라고 규칙을 적어 뒀는데, 캡만 절대 깊이로 남아
//   그 규칙과 어긋나 있었다. 갱 바닥(원지표 아래 최대 47m)에 서면 첫 삽부터 32 를 넘어
//   **게임이 파 준 갱에서 한 칸도 못 파는** 결과가 된다.
const MAX_CUT = PLAYER_CUT_BITS;
const N = PROMOTE_SUB * PROMOTE_SUB;
const CELL_WORDS = N * CAVE_WORDS;  // 셀 하나의 uint32 개수(=256) — 서버 bytea 1024B 와 1:1

const edits = new Map();            // "cgx_cgz" → Uint32Array(64*CAVE_WORDS) 비트마스크

export const editedCellCount = () => edits.size;

// 선두 연속 빈칸 길이 = 위에서 깎여나간 깊이(밴드 수). 지표면은 여기까지 내려와 있다.
// [주의] 인자는 **셀 마스크 배열 + 컬럼 인덱스**다. 예전엔 uint32 값 하나였는데 128비트가
//   number 에 안 들어가 바꿨다 — 값 하나를 넘기던 옛 호출부가 남아 있으면 조용히 0 이 된다.
export const leadCut = (m, k) => colLead(m, k);

export const cellKey = (cgx, cgz) => `${cgx}_${cgz}`;

// X-ray 진단 — 「여기 밑에 판 자리가 있나」.
//
// [왜] X-ray 는 판 자리의 지붕만 벗긴다. 그래서 안 판 땅 위에 서면 **아무 일도 안 일어난다** —
//   그런데 화면으로는 「여기엔 굴이 없다」와 「기능이 고장났다」가 똑같이 생겼다.
//   실제로 2026-08-13 에 그렇게 물으셨고(「엑스레이해도 밑에 암것도 안 보이는데?」),
//   실측하니 그 자리 반경 240m 안에 판 자리가 **0개**였다(최근접 314m). 기능은 멀쩡했다.
//   이 프로젝트가 반복해 밟는 함정이다 — 「어둡다」와 「아직 안 지어졌다」는 픽셀로 안 갈린다.
// [비용] 셀 수만큼 돈다(전 서울 4천). 호출부에서 1초에 한 번만 부른다.
export const carveProbe = (meta, wx, wz, nearR = 240) => {
  const out = { total: edits.size, near: 0, dist: null, under: null };
  if (!meta || !edits.size) return out;
  // ★ 발밑 한 컬럼 — 「내가 선 자리 밑에 굴이 있나」. 눈으로 찾지 않아도 되는 유일한 답이다.
  //   X-ray 는 「보이는 자리」를 열어 주지만, 지상에 서서 「여기 밑이 비었나」를 물으려면
  //   화면이 아니라 컬럼을 직접 읽어야 한다.
  const mk = maskAt(meta, wx, wz);
  if (mk) {
    const lead = colLead(mk.m, mk.k);            // 노천으로 이미 깎인 깊이
    let d = lead;
    while (d < CAVE_BITS && !colBit(mk.m, mk.k, d)) d += 1;   // 그 아래 첫 빈 칸(=굴 천장)
    if (d < CAVE_BITS) {
      let h = 0;
      while (d + h < CAVE_BITS && colBit(mk.m, mk.k, d + h)) h += 1;
      out.under = { top: d, h };                 // 원지표 아래 top m 에 천장, 높이 h m
    }
  }
  const cell = meta.cell || 8;
  let best = Infinity;
  for (const k of edits.keys()) {
    const s = k.indexOf('_');
    const x = Number(k.slice(0, s)) * cell + cell / 2;
    const z = Number(k.slice(s + 1)) * cell + cell / 2;
    const d = Math.hypot(x - wx, z - wz);
    if (d < best) best = d;
    if (d <= nearR) out.near += 1;
  }
  out.dist = Number.isFinite(best) ? best : null;
  return out;
};

// 서버가 뿌린 최종 상태(운영자 굴착)로 덮어쓴다. cut === null 이면 그 셀은 원지형으로 복구된 것.
// 호출부가 넘기는 두 번째 인자(스냅샷 여부)는 읽지 않는다.
// 리틀엔디언 바이트(서버 bytearray 그대로) → uint32 256개.
const b64ToU32 = (s) => {
  const bin = atob(s);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new Uint32Array(u8.buffer);
};

export const applyServerCells = (cells) => {
  if (!Array.isArray(cells) || !cells.length) return;
  for (const c of cells) {
    const k = cellKey(c.cgx, c.cgz);
    if (c.b64) edits.set(k, b64ToU32(c.b64));     // 접속 스냅샷 — 셀 1KB 를 base64 한 줄로
    else if (c.cut == null) edits.delete(k);
    else edits.set(k, Uint32Array.from(c.cut));   // 실시간 델타 — 셀당 256 정수
  }
};

// 월드 XZ → 셀·서브블록 인덱스. **렌더와 같은 격자**를 써야 한다(VOXEL_GRID_OFF 반 칸 이동).
// 어긋나면 파는 자리와 보이는 자리가 반 칸 밀린다.
export const worldToCellSub = (meta, wx, wz) => {
  const fx = (wx - VOXEL_GRID_OFF - meta.origin_x) / meta.cell;
  const fz = (wz - VOXEL_GRID_OFF - meta.origin_z) / meta.cell;
  const ix = Math.floor(fx), iz = Math.floor(fz);
  if (ix < 0 || iz < 0 || ix >= meta.w - 1 || iz >= meta.h - 1) return null;
  const bi = Math.max(0, Math.min(PROMOTE_SUB - 1, Math.floor((fx - ix) * PROMOTE_SUB)));
  const bj = Math.max(0, Math.min(PROMOTE_SUB - 1, Math.floor((fz - iz) * PROMOTE_SUB)));
  return {
    cgx: globalCellIdx(meta.origin_x, meta.cell, ix),
    cgz: globalCellIdx(meta.origin_z, meta.cell, iz),
    bi, bj,
  };
};

// 발밑(heightfieldData) 전용 — 이 위치 컬럼이 얼마나 깎였나(밴드 수).
// 렌더의 promoteCell 과 같은 규칙이어야 발이 구덩이 바닥에 선다(SOP §5-C: 렌더/발밑 동일 규칙).
export const cutAt = (meta, wx, wz) => {
  if (!edits.size) return 0;
  const p = worldToCellSub(meta, wx, wz);
  if (!p) return 0;
  const m = edits.get(cellKey(p.cgx, p.cgz));
  return m ? colLead(m, p.bj * PROMOTE_SUB + p.bi) : 0;
};

// 이 위치 컬럼의 마스크 위치(null = 원지형). 발밑이 동굴 안 solid 를 찾을 때 쓴다.
// 128비트라 값을 못 돌려준다 → **셀 배열과 컬럼 인덱스**를 돌려주고 읽기는 col* 접근자로 한다.
// (호출부가 `if (m)` 로 진위만 보던 자리는 그대로 동작한다 — 편집 없는 컬럼은 null 이다.)
export const maskAt = (meta, wx, wz) => {
  if (!edits.size) return null;
  const p = worldToCellSub(meta, wx, wz);
  if (!p) return null;
  const ck = cellKey(p.cgx, p.cgz);
  const k = p.bj * PROMOTE_SUB + p.bi;
  const m = edits.get(ck);
  if (!m) return null;
  return colAny(m, k) ? { m, k, up: 0 } : null;
};

/** 이 위치가 «속한 셀»에 편집 기록이 있나 — 컬럼 단위가 아니라 **셀 단위**다.
 *
 *  `hfSurfaceYAt` 의 «균일 구간» 보고가 이걸 쓴다. 그 보고는 「이 DEM 셀 안에서 값이
 *  상수다」인데, 판정을 `maskAt`(컬럼 단위)으로 하면 **안 판 컬럼이 셀 전체를 상수라고
 *  보고한다.** 그러면 LOD 격자가 그 값을 셀 끝까지 채우고(가로) 같은 z 띠의 다음 줄을
 *  통째로 베껴(세로) — **판 협곡이 LOD 층에서 최대 3m 다시 메워진다.**
 *  (2026-09-11 실측: 조각 하나의 제 칸 256 중 22개가 1~3m 높았다.) */
export const cellEdited = (meta, wx, wz) => {
  if (!edits.size) return false;
  const p = worldToCellSub(meta, wx, wz);
  if (!p) return false;
  const ck = cellKey(p.cgx, p.cgz);
  return !!edits.get(ck);
};
